/* eslint-disable no-unused-vars */
import React from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Grid';
import { colors } from '@mui/material';

const Process = ({ themeMode = 'light' }) => {
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });

    const GridItemHeadlineBlock = () => (
        <Box>
            <Typography
                sx={{
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    fontSize: '0.875rem',
                }}
                gutterBottom
                color={'secondary'}
            >
                Our Methodology
            </Typography>
            <Box
                component={Typography}
                fontWeight={800}
                variant={'h2'}
                gutterBottom
                data-aos={isMd ? 'fade-right' : 'fade-up'}
                sx={{
                    fontSize: { xs: '2rem', md: '3rem' },
                    lineHeight: 1.2,
                }}
            >
                A Proven Enterprise Delivery Framework
            </Box>
            <Typography
                variant={'h6'}
                component={'p'}
                color={'textSecondary'}
                data-aos={isMd ? 'fade-right' : 'fade-up'}
                sx={{
                    lineHeight: 1.7,
                    maxWidth: '90%',
                }}
            >
                Our structured approach ensures transparency, quality, and timely delivery 
                for enterprise projects. We combine agile methodologies with enterprise-grade 
                governance to deliver mission-critical solutions that exceed expectations.
            </Typography>
        </Box>
    );

    const GridItemReviewBlock = () => {
        const sliderOpts = {
            dots: true,
            infinite: true,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
        };

        return (
            <Box maxWidth={'100%'} data-aos={isMd ? 'fade-left' : 'fade-up'}>
                <Slider {...sliderOpts}>
                    {[
                        {
                            title: 'Discovery & Strategy',
                            subtitle:
                                'We conduct comprehensive stakeholder workshops to understand your business objectives, technical requirements, and compliance needs. Our team develops a detailed roadmap and implementation strategy tailored to your enterprise.',
                            icon: (
                                <svg
                                    width={48}
                                    height={48}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                    />
                                </svg>
                            ),
                            color: colors.amber,
                        },
                        {
                            title: 'Architecture & Design',
                            subtitle:
                                'Our architects design scalable, secure system architectures aligned with enterprise standards. We create detailed technical specifications, security frameworks, and user experience designs that meet compliance requirements.',
                            icon: (
                                <svg
                                    width={48}
                                    height={48}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                                    />
                                </svg>
                            ),
                            color: colors.purple,
                        },
                        {
                            title: 'Implementation & Deployment',
                            subtitle:
                                'We build enterprise-grade solutions using industry best practices, security-first development, and continuous integration. Our solutions are tested, documented, and deployed with zero-downtime strategies and comprehensive monitoring.',
                            icon: (
                                <svg
                                    width={48}
                                    height={48}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            ),
                            color: colors.green,
                        },
                    ].map((item, i) => (
                        <Box padding={{ xs: 1, sm: 2 }} key={i}>
                            <Box
                                component={Card}
                                boxShadow={{ xs: 2, sm: 4 }}
                                borderRadius={3}
                                padding={{ xs: 2, sm: 3, md: 4 }}
                                sx={{
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 6,
                                    },
                                }}
                            >
                                <Box
                                    component={CardContent}
                                    display={'flex'}
                                    flexDirection={'column'}
                                >
                                    <Box
                                        component={Avatar}
                                        variant="rounded"
                                        width={84}
                                        height={84}
                                        marginBottom={2}
                                        bgcolor={
                                            themeMode === 'light'
                                                ? item.color[50]
                                                : item.color[200]
                                        }
                                        borderRadius={5}
                                    >
                                        <Box
                                            color={
                                                item.color[
                                                    themeMode === 'light'
                                                        ? 500
                                                        : 700
                                                ]
                                            }
                                        >
                                            {item.icon}
                                        </Box>
                                    </Box>
                                    <Box
                                        component={Typography}
                                        variant={'h6'}
                                        gutterBottom
                                        fontWeight={500}
                                    >
                                        {item.title}
                                    </Box>
                                    <Typography color="text.secondary">
                                        {item.subtitle}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Slider>
            </Box>
        );
    };

    return (
        <Box>
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Box
                        width={1}
                        height="100%"
                        display="flex"
                        alignItems="center"
                    >
                        <GridItemHeadlineBlock />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box
                        width={1}
                        height="100%"
                        display="flex"
                        alignItems="center"
                    >
                        <GridItemReviewBlock />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

Process.propTypes = {
    themeMode: PropTypes.string.isRequired,
};

export default Process;
